import React, {
  FormEvent,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
} from 'react';

import clsx from 'clsx';
import styles from './PersonalInformation.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { TextInput } from 'components/helpers/Atoms/FormElements/TextInput';
import useDictionary from 'lib/hooks/useDictionary';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import { EditableTabHandle } from '../shared/EditableTabHandle';
import { EditableTabProps } from '../shared/EditableTabProps';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import type { ProfileQuestion } from 'lib/services/user-service';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import {
  CHOOSE_ONE_QUESTION_ANSWER_ID,
  isUnselectedQuestionValue,
} from 'lib/utils/question-options';

const FORM_FIELD_IDS = {
  EMAIL: 'personalInformationEmail',
  ZIP: 'personalInformationZip',
  QUESTION: 'personalInformationQuestion',
} as const;

type QuestionOption = {
  label: string;
  value: string;
};

export interface PersonalInformationProps extends EditableTabProps {
  tabLabelNode?: ReactNode | null;
  headlineNode?: ReactNode | null;
  descriptionNode?: ReactNode | null;

  editHeadlineNode?: ReactNode | null;
  editDescriptionNode?: ReactNode | null;

  emailLabelNode?: ReactNode | null;
  zipCodeLabelNode?: ReactNode | null;
  emailUpdateLabelNode?: ReactNode | null;
  zipCodeUpdateLabelNode?: ReactNode | null;
  questionLabelNode?: ReactNode | null;
  questionPlaceholder?: string;
  questionId?: string;
  questionOptions?: QuestionOption[];

  saveButtonTextNode?: ReactNode | null;
  cancelButtonTextNode?: ReactNode | null;
  successMessageNode?: ReactNode | null;
}

export type PersonalInformationHandle = EditableTabHandle;

type UserDetailApiResponse = {
  Email?: string;
  ZipCode?: string;
  PostalCode?: string;
  Questions?: ProfileQuestion[];
  error?: string;
  error_description?: string;
};

type UpdateUserApiResponse = {
  success: boolean;
  accessToken?: string;
  error?: string;
  error_description?: string;
};

const getQuestionAnswerId = (
  questions: ProfileQuestion[] | undefined,
  questionId: string | undefined
): string => {
  if (!questionId || !questions?.length) {
    return '';
  }

  return questions.find((question) => question.QuestionId === questionId)?.Answer?.AnswerId || '';
};

const upsertQuestionAnswer = (
  questions: ProfileQuestion[],
  questionId: string,
  answerId: string
): ProfileQuestion[] => {
  const filteredQuestions = questions.filter((question) => question.QuestionId !== questionId);

  return [
    ...filteredQuestions,
    {
      QuestionId: questionId,
      Answer: { AnswerId: answerId },
    },
  ];
};

const PersonalInformation = forwardRef<PersonalInformationHandle, PersonalInformationProps>(
  (props, ref) => {
    const {
      tabLabelNode,
      headlineNode,
      descriptionNode,
      editHeadlineNode,
      editDescriptionNode,
      emailLabelNode,
      zipCodeLabelNode,
      questionLabelNode,
      questionId,
      questionOptions,
      saveButtonTextNode,
      cancelButtonTextNode,
      successMessageNode,
      isEditMode,
      onEditModeChange,
      onRequestCancel,
    } = props;

    const titleNode = isEditMode
      ? editHeadlineNode || headlineNode || tabLabelNode
      : headlineNode || tabLabelNode;

    const descriptionContentNode = isEditMode
      ? editDescriptionNode || descriptionNode
      : descriptionNode;

    const { data: session } = useSessionSafe();
    const { getDictionaryValue } = useDictionary();
    const { setFieldError, clearFieldError, clearAllErrors, getFieldError } = useFormErrors();

    const accessToken = session?.user?.accessToken;
    const identityEmail = session?.user?.email;

    // Refs for form fields (for focus management)
    const emailRef = useRef<HTMLInputElement>(null);
    const zipCodeRef = useRef<HTMLInputElement>(null);
    const questionRef = useRef<HTMLSelectElement>(null);

    const [initialEmail, setInitialEmail] = useState(identityEmail || '');
    const [initialZipCode, setInitialZipCode] = useState('');
    const [initialQuestionAnswer, setInitialQuestionAnswer] = useState(
      CHOOSE_ONE_QUESTION_ANSWER_ID
    );

    const [email, setEmail] = useState(identityEmail || '');
    const [zipCode, setZipCode] = useState('');
    const [questionAnswer, setQuestionAnswer] = useState(CHOOSE_ONE_QUESTION_ANSWER_ID);
    const [profileQuestions, setProfileQuestions] = useState<ProfileQuestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState<ReactNode | string>('');
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [pendingFocusField, setPendingFocusField] = useState<
      (typeof FORM_FIELD_IDS)[keyof typeof FORM_FIELD_IDS] | null
    >(null);

    const showQuestionField = !!questionId && !!questionOptions?.length;

    const genericErrorMessage = useMemo(() => {
      return getDictionaryValue(
        'Components.Register.GenericError',
        'There was an error updating your personal information. | Please try again.'
      );
    }, [getDictionaryValue]);

    const validationBannerMessage = useMemo(() => {
      return getDictionaryValue(
        'Components.MyAccount.ErrorMessages.BannerMessage',
        'There was an error signing you up. | Please check the below errors and try again.'
      );
    }, [getDictionaryValue]);

    const questionValidationMessage = useMemo(() => {
      return getDictionaryValue(
        'Components.NewsletterSignup.BusinessIndustryError',
        'Please select a business industry'
      );
    }, [getDictionaryValue]);

    const questionLabelText = questionLabelNode || 'Business Industry';

    const questionDisplayValue = useMemo(() => {
      return questionOptions?.find((option) => option.value === questionAnswer)?.label || '—';
    }, [questionAnswer, questionOptions]);

    const validateEmail = useCallback(
      (emailValue: string) => {
        const trimmed = emailValue.trim();

        if (!trimmed) {
          const message = getDictionaryValue(
            'Components.Register.EmailRequired',
            'Email is required'
          );
          setFieldError(FORM_FIELD_IDS.EMAIL, message);
          return message;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) {
          const message = getDictionaryValue(
            'Components.Register.InvalidEmail',
            'Please enter a valid email'
          );
          setFieldError(FORM_FIELD_IDS.EMAIL, message);
          return message;
        }

        clearFieldError(FORM_FIELD_IDS.EMAIL);
        return '';
      },
      [clearFieldError, getDictionaryValue, setFieldError]
    );

    const validateZip = useCallback(
      (zipValue: string) => {
        const trimmed = zipValue.trim();

        if (!trimmed) {
          const message = getDictionaryValue(
            'Components.Register.ZipCodeRequired',
            'Zipcode is required'
          );
          setFieldError(FORM_FIELD_IDS.ZIP, message);
          return message;
        }

        // Accept either:
        // - 5 to 9 digits (12345, 123456789)
        // - ZIP+4 with hyphen (12345-6789)
        const digitsOnly = trimmed.replace(/-/g, '');

        if (digitsOnly.length < 5 || digitsOnly.length > 9) {
          const message = getDictionaryValue(
            'Components.MyAccount.ErrorMessages.ZipCodeLength',
            'Please enter a zip code between 5 and 9 characters'
          );
          setFieldError(FORM_FIELD_IDS.ZIP, message);
          return message;
        }

        if (trimmed.includes('-') && !/^\d{5}-\d{4}$/.test(trimmed)) {
          const message = getDictionaryValue(
            'Components.Register.InvalidZipCode',
            'Please enter a valid zip code'
          );
          setFieldError(FORM_FIELD_IDS.ZIP, message);
          return message;
        }

        clearFieldError(FORM_FIELD_IDS.ZIP);
        return '';
      },
      [clearFieldError, getDictionaryValue, setFieldError]
    );

    const validateQuestion = useCallback(
      (answerValue: string) => {
        if (!showQuestionField) {
          clearFieldError(FORM_FIELD_IDS.QUESTION);
          return '';
        }

        // The business industry dropdown is always required here, so the shared
        // "Choose one" option is treated the same as the empty placeholder.
        if (isUnselectedQuestionValue(answerValue, true)) {
          setFieldError(FORM_FIELD_IDS.QUESTION, questionValidationMessage);
          return questionValidationMessage;
        }

        clearFieldError(FORM_FIELD_IDS.QUESTION);
        return '';
      },
      [clearFieldError, questionValidationMessage, setFieldError, showQuestionField]
    );

    const fetchUserDetails = useCallback(async () => {
      if (!accessToken) return;
      setIsLoading(true);
      try {
        const response = await fetch('/api/auth/user-detail', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const data = (await response.json()) as UserDetailApiResponse;
        if (!response.ok || data.error) {
          throw new Error(data.error_description || data.error || 'Failed to fetch user detail');
        }

        const apiEmail = (data.Email || '').trim();
        const apiZip = (data.ZipCode || data.PostalCode || '').trim();
        const apiQuestions = Array.isArray(data.Questions) ? data.Questions : [];

        const resolvedEmail = apiEmail || identityEmail || '';
        // Use the previously selected business industry if one exists, otherwise
        // fall back to the Sitecore "Choose one" option.
        const resolvedQuestionAnswer =
          getQuestionAnswerId(apiQuestions, questionId) || CHOOSE_ONE_QUESTION_ANSWER_ID;

        setInitialEmail(resolvedEmail);
        setInitialZipCode(apiZip);
        setInitialQuestionAnswer(resolvedQuestionAnswer);
        setEmail(resolvedEmail);
        setZipCode(apiZip);
        setQuestionAnswer(resolvedQuestionAnswer);
        setProfileQuestions(apiQuestions);
      } catch (e) {
        console.error('[PersonalInformation] Failed to fetch profile:', e);
      } finally {
        setIsLoading(false);
      }
    }, [accessToken, identityEmail, questionId]);

    useEffect(() => {
      fetchUserDetails();
    }, [fetchUserDetails]);

    // Let the error banner (live region) announce before moving focus to the first invalid field.
    useEffect(() => {
      if (!pendingFocusField) return;

      const timer = setTimeout(() => {
        if (pendingFocusField === FORM_FIELD_IDS.EMAIL) {
          emailRef.current?.focus();
          emailRef.current?.select();
        } else if (pendingFocusField === FORM_FIELD_IDS.ZIP) {
          zipCodeRef.current?.focus();
          zipCodeRef.current?.select();
        } else if (pendingFocusField === FORM_FIELD_IDS.QUESTION) {
          questionRef.current?.focus();
        }

        setPendingFocusField(null);
      }, 100);

      return () => clearTimeout(timer);
    }, [pendingFocusField]);

    const isDirty = useCallback(() => {
      return (
        email.trim() !== initialEmail.trim() ||
        zipCode.trim() !== initialZipCode.trim() ||
        questionAnswer !== initialQuestionAnswer
      );
    }, [email, initialEmail, initialQuestionAnswer, initialZipCode, questionAnswer, zipCode]);

    const resetForm = useCallback(() => {
      setEmail(initialEmail);
      setZipCode(initialZipCode);
      setQuestionAnswer(initialQuestionAnswer);
      setSubmitError('');
      setHasAttemptedSubmit(false);
      clearAllErrors();
    }, [clearAllErrors, initialEmail, initialQuestionAnswer, initialZipCode]);

    const handleCancelConfirm = useCallback(() => {
      onEditModeChange(false);
      resetForm();
    }, [onEditModeChange, resetForm]);

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          handleCancelConfirm();
        },
        isDirty,
      }),
      [handleCancelConfirm, isDirty]
    );

    const handleEdit = () => {
      onEditModeChange(true);
      setSubmitError('');
      setSubmitSuccess('');
      setHasAttemptedSubmit(false);
      clearAllErrors();
    };

    const handleCancel = (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (onRequestCancel) {
        onRequestCancel();
      } else {
        handleCancelConfirm();
      }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      clearFieldError(FORM_FIELD_IDS.EMAIL);
    };

    const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setZipCode(event.target.value);
      clearFieldError(FORM_FIELD_IDS.ZIP);
    };

    const handleQuestionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setQuestionAnswer(event.target.value);
      clearFieldError(FORM_FIELD_IDS.QUESTION);
    };

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      setSubmitError('');
      setSubmitSuccess('');
      setHasAttemptedSubmit(true);
      clearAllErrors();

      const emailValidationError = validateEmail(email);
      const questionValidationError = validateQuestion(questionAnswer);
      const zipValidationError = validateZip(zipCode);

      if (emailValidationError || zipValidationError || questionValidationError) {
        const validationErrors = [
          emailValidationError,
          questionValidationError,
          zipValidationError,
        ].filter(Boolean) as string[];

        const summarizedErrors = Array.from(new Set(validationErrors)).join(' ');
        setSubmitError(
          validationBannerMessage
            ? `${validationBannerMessage} ${summarizedErrors}`.trim()
            : summarizedErrors
        );

        // WCAG 2.4.3: move focus to the first invalid field after the banner has announced.
        if (emailValidationError) {
          setPendingFocusField(FORM_FIELD_IDS.EMAIL);
        } else if (questionValidationError) {
          setPendingFocusField(FORM_FIELD_IDS.QUESTION);
        } else if (zipValidationError) {
          setPendingFocusField(FORM_FIELD_IDS.ZIP);
        }

        return;
      }

      if (!accessToken) {
        setSubmitError(genericErrorMessage);
        return;
      }

      const updatedQuestions =
        showQuestionField && questionId
          ? upsertQuestionAnswer(profileQuestions, questionId, questionAnswer)
          : undefined;

      setIsSubmitting(true);

      try {
        const response = await fetch('/api/auth/user-detail', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            // Use the actual current email from the last GET (initialEmail) rather than
            // the session email, which can be stale after a previous successful update.
            identityEmail: initialEmail || identityEmail || email.trim(),
            email: email.trim(),
            zipCode: zipCode.trim(),
            Questions: updatedQuestions,
          }),
        });

        const data = (await response.json()) as UpdateUserApiResponse;
        if (!response.ok || data.success !== true) {
          if (data.error === 'INVALID_ZIP_CODE') {
            setFieldError(
              FORM_FIELD_IDS.ZIP,
              getDictionaryValue('Components.Register.InvalidZipCode', 'Please add valid zipcode')
            );
            return;
          }

          setSubmitError(genericErrorMessage);
          return;
        }

        const updatedEmail = email.trim();
        const updatedZip = zipCode.trim();
        setInitialEmail(updatedEmail);
        setInitialZipCode(updatedZip);
        setInitialQuestionAnswer(questionAnswer);
        if (updatedQuestions) {
          setProfileQuestions(updatedQuestions);
        }

        const successMsg = successMessageNode
          ? successMessageNode
          : getDictionaryValue(
              'Components.MyAccount.ErrorMessages.SuccessfulMessage',
              'Your personal information has been updated.'
            );

        const payload: DataLayerEvent = {
          event: 'profile_update',
        };
        pushToDataLayer(payload);
        setSubmitSuccess(successMsg);
        onEditModeChange(false);
        setHasAttemptedSubmit(false);
        clearAllErrors();
      } catch (err) {
        console.error('[PersonalInformation] Update failed:', err);
        setSubmitError(genericErrorMessage);
      } finally {
        setIsSubmitting(false);
      }
    };

    const emailError = (
      hasAttemptedSubmit ? (getFieldError(FORM_FIELD_IDS.EMAIL) as string | undefined) : undefined
    ) as string | undefined;
    const zipError = (
      hasAttemptedSubmit ? (getFieldError(FORM_FIELD_IDS.ZIP) as string | undefined) : undefined
    ) as string | undefined;
    const questionError = (
      hasAttemptedSubmit
        ? (getFieldError(FORM_FIELD_IDS.QUESTION) as string | undefined)
        : undefined
    ) as string | undefined;

    const emailErrorId = emailError ? `${FORM_FIELD_IDS.EMAIL}-error` : undefined;
    const zipErrorId = zipError ? `${FORM_FIELD_IDS.ZIP}-error` : undefined;
    const questionErrorId = questionError ? `${FORM_FIELD_IDS.QUESTION}-error` : undefined;

    return (
      <div className={styles.personalInformation} aria-busy={isLoading ? 'true' : undefined}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              {titleNode && <h2 className={styles.title}>{titleNode}</h2>}

              {descriptionContentNode && (
                <div className={styles.description}>{descriptionContentNode}</div>
              )}
            </div>

            {!isEditMode && (
              <CtaButton
                ctaStyle="link"
                ctaVariant="brand"
                type="button"
                className={styles.editButton}
                onClick={handleEdit}
              >
                {/* aria-hidden hides the decorative icon from the accessible name computation */}
                <SvgIcon
                  icon="write-review"
                  viewBox="0 0 14 14"
                  className={styles.editIcon}
                  aria-hidden="true"
                />
                <span>{getDictionaryValue('Components.MyAccount.Edit', 'Edit')}</span>
              </CtaButton>
            )}
          </div>
        </div>

        <hr className={styles.divider} />

        {!isEditMode ? (
          <>
            {submitSuccess && (
              <MessageBanner
                type="success"
                message={submitSuccess}
                className={styles.successBanner}
                autoFocus
              />
            )}

            <div
              className={clsx(styles.defaultView, showQuestionField && styles.defaultViewTwoColumn)}
            >
              <div className={styles.emailZipGroup}>
                <div className={styles.readOnlyRow}>
                  <label className={styles.readOnlyLabel}>
                    {emailLabelNode || 'Email address'}
                  </label>
                  <div className={styles.readOnlyValue}>{email || '—'}</div>
                </div>

                <div className={styles.readOnlyRow}>
                  <label className={styles.readOnlyLabel}>{zipCodeLabelNode || 'Zip code'}</label>
                  <div className={styles.readOnlyValue}>{zipCode || '—'}</div>
                </div>
              </div>

              {showQuestionField && (
                <div className={styles.questionGroup}>
                  <div className={styles.readOnlyRow}>
                    <label className={styles.readOnlyLabel}>{questionLabelText}</label>
                    <div className={styles.readOnlyValue}>{questionDisplayValue}</div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <span className={styles.requiredIndicator}>
              {getDictionaryValue('Components.MyAccount.Required', '* Required')}
            </span>

            {submitError && <MessageBanner type="error" message={submitError} />}

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={clsx(styles.inputs, showQuestionField && styles.inputsTwoColumn)}>
                <FormField
                  label={emailLabelNode || 'Email address'}
                  labelFor={FORM_FIELD_IDS.EMAIL}
                  error={!!emailError}
                  className={styles.emailField}
                  required
                >
                  <TextInput
                    ref={emailRef}
                    id={FORM_FIELD_IDS.EMAIL}
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    disabled={isSubmitting}
                    error={!!emailError}
                    errorMessage={emailError}
                    errorId={emailErrorId}
                    required
                  />
                </FormField>

                {showQuestionField && (
                  <div className={styles.questionGroup}>
                    <FormField
                      label={questionLabelText}
                      labelFor={FORM_FIELD_IDS.QUESTION}
                      error={!!questionError}
                      required
                    >
                      <div className={styles.questionFieldControl}>
                        <div
                          className={
                            styles.selectWrapper +
                            (questionError ? ` ${styles.selectWrapperErrorHighlight}` : '')
                          }
                        >
                          <select
                            ref={questionRef}
                            id={FORM_FIELD_IDS.QUESTION}
                            value={questionAnswer}
                            onChange={handleQuestionChange}
                            disabled={isSubmitting}
                            className={styles.questionSelect}
                            aria-invalid={!!questionError}
                            aria-describedby={questionError ? questionErrorId : undefined}
                            required
                          >
                            {(questionOptions || []).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <span className={styles.selectIcon} aria-hidden="true">
                            <SvgIcon icon="chevron-down" size="sm" />
                          </span>
                        </div>
                        {questionError && (
                          <div id={questionErrorId} className={styles.fieldErrorText}>
                            {questionError}
                          </div>
                        )}
                      </div>
                    </FormField>
                  </div>
                )}

                <FormField
                  label={zipCodeLabelNode || 'Zip code'}
                  labelFor={FORM_FIELD_IDS.ZIP}
                  error={!!zipError}
                  className={styles.zipField}
                  required
                >
                  <TextInput
                    ref={zipCodeRef}
                    id={FORM_FIELD_IDS.ZIP}
                    type="text"
                    inputMode="text"
                    value={zipCode}
                    onChange={handleZipCodeChange}
                    disabled={isSubmitting}
                    error={!!zipError}
                    errorMessage={zipError}
                    errorId={zipErrorId}
                    required
                  />
                </FormField>
              </div>

              <div className={styles.actions}>
                <CtaButton
                  ctaStyle="filled"
                  ctaVariant="brand"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {saveButtonTextNode || 'Save changes'}
                </CtaButton>

                <CtaButton
                  ctaStyle="link"
                  ctaVariant="brand"
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleCancel}
                >
                  {cancelButtonTextNode || 'Cancel'}
                </CtaButton>
              </div>
            </form>
          </>
        )}
      </div>
    );
  }
);

PersonalInformation.displayName = 'PersonalInformation';

export default PersonalInformation;
